/**
 * @fileoverview `usePopover` — hook variant of the Popover primitive.
 *
 * Manages: controlled open state, modal vs non-modal mode, ARIA wiring
 * (aria-expanded / aria-haspopup on the trigger, role on the content),
 * and dismiss behavior. Returns prop spreaders for the trigger and
 * content elements plus the floating-ui-base refs/styles.
 *
 * Focus management (modal mode) is handled by the declarative
 * `<Popover>` component via floating-ui's `FloatingFocusManager`.
 * Hook consumers who want focus trap need to render the focus
 * manager themselves (see Popover.jsx for the canonical pattern).
 *
 * @module @puffin-core/popover/usePopover
 */

import { useCallback, useEffect, useId, useRef } from 'react';
import { useFloatingPosition } from '@puffin-core/floating-ui-base';

const DEFAULT_PLACEMENT = 'bottom-start';

/**
 * Was focus LOST by the close — dropped on `<body>` because the focused element unmounted with
 * the content, or stranded inside a floating element a hook consumer keeps mounted?
 *
 * Anything else means somebody put focus there on purpose (the consumer's pick handler, an
 * outside press on another control), and the hook must not tug it back.
 *
 * @param {Element|null} floating - The floating element, when it is still mounted.
 * @returns {boolean}
 */
function focusWasLost(floating) {
  const active = document.activeElement;
  if (!active || active === document.body) return true;
  return Boolean(floating && floating.contains(active));
}

/**
 * Focus the trigger. floating-ui accepts ANY Element as the reference, and a foreign-namespace
 * one has no `.focus` — it is skipped, never called.
 *
 * @param {Object} refs - floating-ui refs.
 * @returns {void}
 */
function focusTrigger(refs) {
  const trigger = refs.domReference.current;
  if (trigger && typeof trigger.focus === 'function') trigger.focus();
}

/**
 * `returnFocusOnClose` — on EVERY open → closed transition, whoever caused it: floating-ui
 * (Escape, an outside press) or the consumer itself (`setOpen(false)` when a menu item is
 * picked — R6-B session 8, unit 1b / Q-R6C-9; the `onOpenChange` wrapper this replaces never saw
 * that close, so a picked item left focus on `<body>`). It runs after the close has COMMITTED and
 * refocuses the trigger only when focus was lost, so a consumer that moved focus wins.
 *
 * @param {Object} args
 * @param {boolean} args.enabled - The `returnFocusOnClose` option.
 * @param {boolean} args.open - Controlled open state.
 * @param {Object} args.refs - floating-ui refs (`domReference`, `floating`).
 * @returns {void}
 */
function useReturnFocusOnClose({ enabled, open, refs }) {
  const wasOpenRef = useRef(Boolean(open));
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = Boolean(open);
    if (!enabled || !wasOpen || open) return;
    if (focusWasLost(refs.floating.current)) focusTrigger(refs);
  }, [enabled, open, refs]);
}

/**
 * @param {Object} options
 * @param {boolean} options.open - Controlled open state.
 * @param {(open: boolean) => void} options.onOpenChange
 * @param {boolean} [options.modal=false] - Modal mode applies
 *   role="dialog" + aria-modal="true" + aria-haspopup="dialog" on the
 *   trigger. Non-modal applies role="region".
 * @param {string} [options.placement='bottom-start']
 * @param {('absolute'|'fixed')} [options.strategy='absolute'] - The
 *   CSS positioning strategy forwarded to the base hook. Pass `'fixed'`
 *   for an in-flow popover whose trigger sits inside an
 *   `overflow: auto | hidden` clipping ancestor (DRP-001) so the
 *   floating content escapes the clip chain via viewport-relative
 *   `position: fixed`.
 * @param {boolean} [options.fitViewport=true] - Cap the popover to the
 *   height available before the viewport edge and scroll internally
 *   (ONB-COACH-UX-001). Forwarded to the base hook's `size()` middleware,
 *   which writes `--available-height` on the floating node; `.popover`
 *   reads it (`max-height: var(--available-height)`, with `--available-height:
 *   85vh` declared on `.popover` as the pre-set default since
 *   CSS-FALLBACK-STRIP-001 — this middleware's write is an INLINE custom
 *   property, which beats that declaration). Default ON —
 *   a tall menu/list/selector popover no longer overflows the viewport.
 *   Opt OUT with `false` to fall back to the static 85vh cap.
 * @param {boolean} [options.dismissOnEscape=true]
 * @param {boolean} [options.dismissOnOutsideClick=true]
 * @param {boolean} [options.enableClick=true] - The popover owns its
 *   open-on-click toggle (forwarded to the base hook's `useClick`).
 *   Consumers must NOT hand-roll an onClick on the trigger.
 * @param {Object|null} [options.listNavigation=null] - Opt IN to
 *   in-floating roving keyboard navigation (Arrow / Home / End move a
 *   roving focus across the registered item refs). Default OFF — when
 *   omitted, the popover is byte-equivalent to its pre-listNav self, so
 *   the menu/listbox consumers that hand-roll `focusedIdx` + a
 *   `document` keydown listener today are unaffected until they migrate.
 *   The descriptor is forwarded verbatim to `useFloatingPosition`'s
 *   `listNavigation` option ({`listRef`, `activeIndex`, `onNavigate`,
 *   and the `loop` / `focusItemOnOpen` / `virtual` / `selectedIndex` /
 *   `nested` / `orientation` flags}). When supplied, the returned
 *   `itemProps(index, extra?)` spreader MUST be applied to each item
 *   element so floating-ui can wire its keyboard handlers + (in
 *   `virtual` mode) `aria-activedescendant`.
 * @param {boolean} [options.returnFocusOnClose=false] - Opt IN to
 *   returning DOM focus to the trigger when the popover transitions
 *   open → closed. A NON-modal popover does not return focus for free
 *   (that is `FloatingFocusManager`'s modal-only job). When `true`, the
 *   hook refocuses `refs.domReference` after ANY close has committed —
 *   floating-ui's (Escape, outside press) or the consumer's own
 *   `setOpen(false)` on an item pick — and only when focus was LOST
 *   (on `<body>`, or stranded inside the floating element): a consumer
 *   whose action moved focus deliberately keeps it. Default OFF so a
 *   selector that navigates away on select is unaffected.
 * @param {('menu'|'listbox'|'tree'|'grid'|'dialog')} [options.triggerHasPopup] -
 *   Overrides the `aria-haspopup` kind advertised on the trigger. Defaults to
 *   `'dialog'` in modal mode and `'menu'` otherwise. A selector popover whose
 *   content is a `role="listbox"` should pass `'listbox'` so the trigger
 *   advertises the matching popup kind (WAI-ARIA listbox-button pattern).
 * @param {string} [options.id] - Optional explicit content id.
 * @param {string} [options.titleId] - Optional id of an in-content
 *   `<h2>`/`<h3>`/etc. that titles the popover. When supplied, the
 *   popover gets `aria-labelledby={titleId}`.
 * @returns {{
 *   isOpen: boolean,
 *   contentId: string,
 *   triggerProps: (extra?: Object) => Object,
 *   contentProps: (extra?: Object) => Object,
 *   itemProps: (index: number, extra?: Object) => Object,
 *   close: () => void,
 *   refs: ReturnType<typeof useFloatingPosition>['refs'],
 *   floatingStyles: React.CSSProperties,
 *   context: ReturnType<typeof useFloatingPosition>['context'],
 *   placement: string,
 * }}
 */
export function usePopover(options) {
  const {
    open,
    onOpenChange,
    modal = false,
    placement = DEFAULT_PLACEMENT,
    strategy,
    // ONB-COACH-UX-001 — popovers fit the viewport by default (dynamic
    // `--available-height`); opt out to the static 85vh CSS cap with `false`.
    fitViewport = true,
    dismissOnEscape = true,
    dismissOnOutsideClick = true,
    // Pass-through to the base hook: the factory owns the open-on-click toggle.
    // A composite that drives the toggle through its own composed handler
    // (e.g. NavFlyout's tooltip+popover interleave) opts out with `false`.
    enableClick = true,
    // Opt-IN roving keyboard navigation + focus-return-to-trigger. Both default
    // OFF so existing consumers are byte-unaffected (mirrors how `enableClick`
    // and `strategy` were introduced additively).
    listNavigation = null,
    returnFocusOnClose = false,
    triggerHasPopup,
    id,
    titleId,
  } = options;

  const generatedId = useId();
  const contentId = id || `popover-${generatedId}`;

  const {
    refs,
    floatingStyles,
    context,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
    placement: actualPlacement,
  } = useFloatingPosition({
    open,
    onOpenChange,
    placement,
    strategy,
    fitViewport,
    dismissOnEscape,
    dismissOnOutsideClick,
    enableClick,
    listNavigation,
  });

  // A NON-modal popover does not return focus for free (that is
  // `FloatingFocusManager`'s modal-only job).
  useReturnFocusOnClose({ enabled: returnFocusOnClose, open, refs });

  // The ITEM-PICK seam. A menu closed from a picked item calls `close()` BEFORE it runs the
  // item's action: with `returnFocusOnClose`, focus is on the trigger WHILE the action runs, so a
  // dialog the action opens captures the trigger as its opener — not a menu item that is about
  // to unmount (which is where the dialog would try, and fail, to return focus on close).
  const close = useCallback(() => {
    if (returnFocusOnClose) focusTrigger(refs);
    onOpenChange(false);
  }, [returnFocusOnClose, refs, onOpenChange]);

  const triggerProps = useCallback(
    (extra = {}) => {
      const baseProps = getReferenceProps(extra);
      return {
        ...baseProps,
        'aria-expanded': open ? 'true' : 'false',
        'aria-controls': contentId,
        // Always advertise the popup kind on the trigger — `dialog` for modal,
        // `menu` for the non-modal menu/list popovers (NavKebabMenu, the matrix
        // assign picker). A selector popover (content is a `role="listbox"`)
        // overrides this with `triggerHasPopup: 'listbox'`. Consumers no longer
        // hand-roll aria-haspopup.
        'aria-haspopup': triggerHasPopup || (modal ? 'dialog' : 'menu'),
      };
    },
    [getReferenceProps, open, contentId, modal, triggerHasPopup],
  );

  const contentProps = useCallback(
    (extra = {}) => {
      // floating-ui's `useListNavigation` adds `aria-orientation` to the FLOATING element's
      // props, assuming that element IS the menu / listbox. Here it is the `role="region"` /
      // `role="dialog"` wrapper, and neither role admits the attribute (axe `aria-allowed-attr`
      // — R6-B session 12, the first axe run over an OPEN listNavigation popover). The
      // consumer's `role="menu"` child is vertical by default, so nothing is lost by dropping it.
      // A typed binding, not a literal: floating-ui's getters take `React.HTMLProps`, which
      // names no `data-*` attribute.
      /** @type {React.HTMLProps<HTMLElement> & {'data-placement': string}} */
      const requested = {
        ...extra,
        id: contentId,
        role: modal ? 'dialog' : 'region',
        'data-placement': actualPlacement,
        ...(modal ? { 'aria-modal': 'true' } : {}),
        ...(titleId ? { 'aria-labelledby': titleId } : {}),
      };
      const { 'aria-orientation': _orientation, ...floatingProps } = getFloatingProps(requested);
      return floatingProps;
    },
    [getFloatingProps, contentId, modal, actualPlacement, titleId],
  );

  // Per-item prop spreader for roving keyboard navigation. floating-ui's
  // `getItemProps` wires the keydown handlers + (virtual mode)
  // `aria-activedescendant` so the item array becomes Arrow/Home/End
  // navigable. A no-op spreader passthrough when `listNavigation` is OFF (the
  // base hook still returns a `getItemProps`, but with the interaction
  // disabled it adds nothing) — consumers only call this when they opt in.
  const itemProps = useCallback(
    (index, extra = {}) => {
      /** @type {React.HTMLProps<HTMLElement> & {'data-index': number}} */
      const requested = { ...extra, 'data-index': index };
      return getItemProps(requested);
    },
    [getItemProps],
  );

  return {
    isOpen: Boolean(open),
    contentId,
    triggerProps,
    contentProps,
    itemProps,
    close,
    refs,
    floatingStyles,
    context,
    placement: actualPlacement,
  };
}
